import { useMemo } from "react";
import { Link, useNavigate } from "react-router";
import { orders as ordersApi } from "../api/client";
import type { OrderSummary } from "../api/types";
import { useMe } from "../auth/session";
import { Page } from "../components/Shell";
import { Empty, ErrorBox, Icon, Kpi, Spinner, StatePill } from "../components/ui";
import { useAsync } from "../hooks/useAsync";
import { fmt, t } from "../i18n";
import { dateShort, dateTimeShort, monthName } from "../lib/format";
import { brl, brlRound, toBig, usd } from "../lib/money";
import { countryName, railLabel } from "../lib/rail";

export function supplierName(o: OrderSummary): string {
  return o.beneficiary?.legalName ?? o.beneficiaryName ?? "—";
}

export function Painel() {
  const me = useMe();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useAsync(() => ordersApi.list({ page: 1 }), []);
  const stats = useAsync((signal) => ordersApi.stats(signal), []);
  const items = data?.items ?? [];

  // KPIs vêm do servidor (todos os pedidos da empresa, mês civil de America/Sao_Paulo, limite atualizado)
  const kpi = useMemo(() => {
    const s = stats.data;
    const monthly = toBig(s?.limits.monthlyCents ?? me.company?.limits.monthlyCents);
    const avail = toBig(s?.availableCents ?? (monthly - toBig(me.company?.monthUsedCents)).toString());
    return {
      settledCount: s?.settledCount ?? 0, paidBrl: toBig(s?.paidBrlCents), paidUsd: toBig(s?.paidUsdCents),
      sending: s?.sendingCount ?? 0, review: s?.reviewCount ?? 0,
      savings: s && s.settledCount > 0 ? toBig(s.savingsCents) : null, monthly, avail: avail < 0n ? 0n : avail,
    };
  }, [stats.data, me.company]);

  const recent = items.slice(0, 10);

  return (
    <Page
      title={t.panel.title}
      actions={
        <Link to="/pagamentos/novo" className="btn">
          {Icon.plus()}
          {t.panel.newPayment}
        </Link>
      }
    >
      <div className="grid4">
        <Kpi label={t.panel.kpiPaid} value={brl(kpi.paidBrl)} sub={fmt(t.panel.kpiPaidSub, { n: kpi.settledCount, usd: usd(kpi.paidUsd) })} />
        <Kpi label={t.panel.kpiOngoing} value={kpi.sending + kpi.review} tone="go" sub={fmt(t.panel.kpiOngoingSub, { sending: kpi.sending, review: kpi.review })} />
        <Kpi label={t.panel.kpiSavings} value={kpi.savings === null ? "—" : brl(kpi.savings)} tone="ok" sub={t.panel.kpiSavingsSub} />
        <Kpi label={t.panel.kpiLimit} value={brlRound(kpi.avail)} sub={fmt(t.panel.kpiLimitSub, { monthly: brlRound(kpi.monthly) })} />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t.panel.recent}</h2>
          <div className="row">
            <Link to="/pagamentos" className="btn ghost sm">{t.panel.seeAll}</Link>
            <a className="btn ghost sm" href={ordersApi.exportCsvUrl()} download>
              {Icon.download()}
              {t.common.export}
            </a>
          </div>
        </div>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}>
            <ErrorBox error={error} onRetry={reload} />
          </div>
        ) : recent.length === 0 ? (
          <Empty
            title={t.panel.emptyTitle}
            body={t.panel.emptyBody}
            action={
              <Link to="/fornecedores" className="btn ghost">
                {t.nav.fornecedores}
              </Link>
            }
          />
        ) : (
          <>
            <div className="tscroll desk-table">
              <table>
                <thead>
                  <tr>
                    <th>{t.panel.colSupplier}</th>
                    <th>{t.panel.colUsd}</th>
                    <th>{t.panel.colBrl}</th>
                    <th>{t.panel.colStatus}</th>
                    <th>{t.panel.colDate}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {recent.map((o) => (
                    <tr key={o.id} className="link" onClick={() => navigate(`/pagamentos/${o.id}`)}>
                      <td>
                        <div className="cell2">
                          <b>{supplierName(o)}</b>
                          <span>
                            {o.beneficiary ? `${countryName(o.beneficiary.country)} · ${railLabel(o.beneficiary.rail)}` : o.reference ?? o.id}
                          </span>
                        </div>
                      </td>
                      <td className="mono">{usd(o.usdCents)}</td>
                      <td className="mono">{o.state === "expired" ? "—" : brl(o.totalCents)}</td>
                      <td>
                        <StatePill state={o.state} />
                      </td>
                      <td className="muted" style={{ whiteSpace: "nowrap" }}>
                        {dateTimeShort(o.settledAt ?? o.createdAt)}
                      </td>
                      <td className="r">
                        <Link to={`/pagamentos/${o.id}`} className="linkbtn" onClick={(e) => e.stopPropagation()}>
                          {t.common.open}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mob-list">
              {recent.map((o) => (
                <Link key={o.id} to={`/pagamentos/${o.id}`} className="item">
                  <div className="l">
                    <b>{supplierName(o)}</b>
                    <span className="mono">
                      {usd(o.usdCents)} · {o.state === "expired" ? "—" : brl(o.totalCents)}
                    </span>
                  </div>
                  <div className="r">
                    <StatePill state={o.state} />
                    <small>{dateShort(o.settledAt ?? o.createdAt)}</small>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
      <p className="legal">{fmt(t.panel.monthLabel, { month: monthName(new Date()), company: me.company?.tradeName || me.company?.legalName })}. {t.brand.legalFooter}</p>
    </Page>
  );
}
